import type {TimeSeriesS} from './history.ts';
/* eslint import-x/no-duplicates: "off"
   --------
   @typescript-eslint/consistent-type-imports conflicts with this
   rule when only some imports are type-only from the same file. */
import {Direction} from './optimisation.ts';
import type {RetirementConfig} from './retirement_config.ts';

export type SingleHistory = {
  asset_history: TimeSeriesS<number>;
  flow_history: TimeSeriesS<number>;
  exhausted: boolean;
  exhaustion_year: number;
  max_year: number;
};

type Input = {
  seq: number;
  input: number;
  config: RetirementConfig;
};

type Result = {
  seq: number;
  input: number;
  metric: number;
};

export function directionalGuidanceFactory(
  getInput: (config: RetirementConfig) => number,
  getConfig: (config: RetirementConfig, input: number) => RetirementConfig,
  calculateMetric: (config: RetirementConfig) => number,
  lowerInputLimit: number,
  upperInputLimit: number,
  sampleCount: number,
  inputInterval: number,
): (baselineConfig: RetirementConfig) => Direction {
  return (baselineConfig: RetirementConfig) => {
    const baselineInput = getInput(baselineConfig);

    const inputs: Input[] = [...Array.from({length: sampleCount}).keys()]
      .map(i => i - (sampleCount / 2))
      .map(seq => {
        const input = (seq * inputInterval) + baselineInput;
        const config = getConfig(baselineConfig, input);
        return {seq, input, config};
      })
      .filter(i => i.input >= lowerInputLimit && i.input <= upperInputLimit);

    const results = new Map<number, Result>(
      inputs.map(i => {
        const metric = calculateMetric(i.config);

        return [
          i.seq,
          {
            seq: i.seq,
            input: i.input,
            metric,
          },
        ];
      }),
    );

    const baselineMetric = results.get(0)!.metric;
    function getDirection(slice: Result[]): Direction {
      let direction = Direction.NO_GUIDANCE;

      for (const result of slice) {
        // A higher metric is always better.
        if (result.metric < baselineMetric) {
          direction = Direction.SMALLER;
          break;
        }

        if (result.metric > baselineMetric) {
          direction = Direction.LARGER;
          break;
        }
      }

      return direction;
    }

    /* eslint @typescript-eslint/require-array-sort-compare: "off" */
    const left = getDirection([...results.values()].filter(r => r.seq < 0).sort().reverse());
    const right = getDirection([...results.values()].filter(r => r.seq > 0).sort());

    if (left === Direction.SMALLER && right === Direction.SMALLER) {
      return Direction.NO_GUIDANCE;
    }

    if (left === Direction.LARGER && right === Direction.LARGER) {
      return Direction.NO_GUIDANCE;
    }

    if (right === Direction.LARGER && baselineInput < upperInputLimit) {
      return Direction.LARGER;
    }

    if (left === Direction.LARGER && baselineInput > lowerInputLimit) {
      return Direction.SMALLER;
    }

    return Direction.NO_GUIDANCE;
  };
}
